export const WEB_AR_SCHEMA_VERSION = 1 as const;
export const WEB_AR_ENGINE_PACKAGE = "@8thwall/engine-binary@1.0.0" as const;

export interface WebArLaunchPayload {
  readonly schemaVersion: 1;
  readonly launchGrantId: string;
  readonly composition: {
    readonly compositionId: string;
    readonly revisionId: string;
    readonly configurationSha256: string;
  };
  readonly release: {
    readonly releaseId: string;
    readonly manifestSha256: string;
  };
  readonly model: {
    readonly url: string;
    readonly sha256: string;
    readonly byteLength: number;
    readonly mediaType: "model/gltf-binary";
  };
  readonly scale: {
    readonly minimum: number;
    readonly maximum: number;
    readonly initial: number;
    readonly step: number;
  };
  readonly placement: {
    readonly mode: "horizontal";
    readonly initialDistanceMeters: number;
  };
  readonly origin: string;
  readonly issuedAt: string;
  readonly expiresAt: string;
}

export type WebArState =
  | "idle"
  | "resolving_launch"
  | "requesting_camera"
  | "loading_engine"
  | "loading_model"
  | "starting_tracking"
  | "active"
  | "error"
  | "exited";

export type WebArFailureCode =
  | "launch_missing"
  | "launch_rejected"
  | "launch_replayed"
  | "origin_rejected"
  | "expired"
  | "unsupported"
  | "camera_denied"
  | "engine_load_failed"
  | "model_load_failed"
  | "model_integrity_failed"
  | "runtime_failed"
  | "cancelled";

export type WebArEvent =
  | { readonly type: "intent_received"; readonly at: string }
  | {
      readonly type: "launch_resolved";
      readonly at: string;
      readonly launchGrantId: string;
      readonly releaseId: string;
      readonly compositionId: string;
    }
  | { readonly type: "camera_granted"; readonly at: string }
  | { readonly type: "engine_loaded"; readonly at: string; readonly package: string }
  | {
      readonly type: "model_verified";
      readonly at: string;
      readonly sha256: string;
      readonly byteLength: number;
    }
  | { readonly type: "tracking_started"; readonly at: string }
  | {
      readonly type: "placement_changed";
      readonly at: string;
      readonly reason: "initial" | "tap" | "recenter";
    }
  | { readonly type: "scale_changed"; readonly at: string; readonly scale: number }
  | {
      readonly type: "failure";
      readonly at: string;
      readonly code: WebArFailureCode;
      readonly recoverable: boolean;
      readonly stage: WebArState;
    }
  | {
      readonly type: "fallback_exit";
      readonly at: string;
      readonly destination: "storefront_3d";
    };

export class WebArError extends Error {
  constructor(
    readonly code: WebArFailureCode,
    message: string,
    readonly recoverable = true,
    options?: ErrorOptions
  ) {
    super(message, options);
    this.name = "WebArError";
  }
}

export interface LaunchValidationContext {
  readonly now: Date;
  readonly trustedModelOrigins: readonly string[];
  readonly trustedRequestOrigins: readonly string[];
  readonly maximumLifetimeMs?: number;
  readonly maximumClockSkewMs?: number;
}

type UnknownRecord = Record<string, unknown>;

const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{5,127}$/u;
const SHA256_PATTERN = /^[a-f0-9]{64}$/u;

function asRecord(value: unknown, label: string): UnknownRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new WebArError("launch_rejected", `${label} must be an object.`, false);
  }
  return value as UnknownRecord;
}

function exactKeys(value: UnknownRecord, keys: readonly string[], label: string): void {
  const expected = new Set(keys);
  if (Object.keys(value).some((key) => !expected.has(key)) ||
      keys.some((key) => !(key in value))) {
    throw new WebArError("launch_rejected", `${label} has an invalid shape.`, false);
  }
}

function asString(value: unknown, label: string, maximumLength = 256): string {
  if (typeof value !== "string" || value.length === 0 || value.length > maximumLength) {
    throw new WebArError("launch_rejected", `${label} is invalid.`, false);
  }
  return value;
}

function asId(value: unknown, label: string): string {
  const result = asString(value, label, 128);
  if (!ID_PATTERN.test(result)) {
    throw new WebArError("launch_rejected", `${label} is invalid.`, false);
  }
  return result;
}

function asSha256(value: unknown, label: string): string {
  const result = asString(value, label, 64);
  if (!SHA256_PATTERN.test(result)) {
    throw new WebArError("launch_rejected", `${label} is invalid.`, false);
  }
  return result;
}

function asFiniteNumber(
  value: unknown,
  label: string,
  minimum: number,
  maximum: number
): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < minimum || value > maximum) {
    throw new WebArError("launch_rejected", `${label} is invalid.`, false);
  }
  return value;
}

function asInteger(value: unknown, label: string, minimum: number, maximum: number): number {
  const result = asFiniteNumber(value, label, minimum, maximum);
  if (!Number.isSafeInteger(result)) {
    throw new WebArError("launch_rejected", `${label} is invalid.`, false);
  }
  return result;
}

function parseTimestamp(value: unknown, label: string): { readonly text: string; readonly time: number } {
  const text = asString(value, label, 64);
  const time = Date.parse(text);
  if (!Number.isFinite(time) || new Date(time).toISOString() !== text) {
    throw new WebArError("launch_rejected", `${label} is invalid.`, false);
  }
  return { text, time };
}

export function normalizeHttpsOrigin(value: string, label: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch (cause) {
    throw new WebArError("origin_rejected", `${label} is invalid.`, false, { cause });
  }
  if (
    url.protocol !== "https:" ||
    url.username !== "" ||
    url.password !== "" ||
    url.pathname !== "/" ||
    url.search !== "" ||
    url.hash !== "" ||
    url.origin !== value
  ) {
    throw new WebArError("origin_rejected", `${label} must be a canonical HTTPS origin.`, false);
  }
  return url.origin;
}

function parseModelUrl(value: unknown, trustedOrigins: ReadonlySet<string>): string {
  const text = asString(value, "model.url", 2048);
  let url: URL;
  try {
    url = new URL(text);
  } catch (cause) {
    throw new WebArError("origin_rejected", "The model URL is invalid.", false, { cause });
  }
  if (
    url.protocol !== "https:" ||
    url.username !== "" ||
    url.password !== "" ||
    url.hash !== "" ||
    !trustedOrigins.has(url.origin)
  ) {
    throw new WebArError("origin_rejected", "The model origin is not trusted.", false);
  }
  return url.href;
}

export function parseWebArLaunchPayload(
  input: unknown,
  context: LaunchValidationContext
): WebArLaunchPayload {
  const root = asRecord(input, "launch payload");
  exactKeys(root, [
    "schemaVersion",
    "launchGrantId",
    "composition",
    "release",
    "model",
    "scale",
    "placement",
    "origin",
    "issuedAt",
    "expiresAt"
  ], "launch payload");
  if (root.schemaVersion !== WEB_AR_SCHEMA_VERSION) {
    throw new WebArError("launch_rejected", "The launch schema is unsupported.", false);
  }

  const trustedModelOrigins = new Set(
    context.trustedModelOrigins.map((origin) => normalizeHttpsOrigin(origin, "trusted model origin"))
  );
  const trustedRequestOrigins = new Set(
    context.trustedRequestOrigins.map((origin) => normalizeHttpsOrigin(origin, "trusted request origin"))
  );
  if (trustedModelOrigins.size === 0 || trustedRequestOrigins.size === 0) {
    throw new WebArError("origin_rejected", "No trusted launch origins are configured.", false);
  }

  const origin = normalizeHttpsOrigin(asString(root.origin, "origin", 512), "origin");
  if (!trustedRequestOrigins.has(origin)) {
    throw new WebArError("origin_rejected", "The requesting storefront origin is not trusted.", false);
  }

  const issuedAt = parseTimestamp(root.issuedAt, "issuedAt");
  const expiresAt = parseTimestamp(root.expiresAt, "expiresAt");
  const maximumLifetimeMs = context.maximumLifetimeMs ?? 10 * 60_000;
  const maximumClockSkewMs = context.maximumClockSkewMs ?? 30_000;
  if (issuedAt.time > context.now.getTime() + maximumClockSkewMs) {
    throw new WebArError("launch_rejected", "The launch was issued in the future.", false);
  }
  if (
    expiresAt.time <= context.now.getTime() ||
    expiresAt.time <= issuedAt.time ||
    expiresAt.time - issuedAt.time > maximumLifetimeMs
  ) {
    throw new WebArError("expired", "The AR launch has expired.", true);
  }

  const composition = asRecord(root.composition, "composition");
  exactKeys(
    composition,
    ["compositionId", "revisionId", "configurationSha256"],
    "composition"
  );
  const release = asRecord(root.release, "release");
  exactKeys(release, ["releaseId", "manifestSha256"], "release");
  const model = asRecord(root.model, "model");
  exactKeys(model, ["url", "sha256", "byteLength", "mediaType"], "model");
  if (model.mediaType !== "model/gltf-binary") {
    throw new WebArError("launch_rejected", "Only binary GLB models are accepted.", false);
  }

  const scale = asRecord(root.scale, "scale");
  exactKeys(scale, ["minimum", "maximum", "initial", "step"], "scale");
  const minimum = asFiniteNumber(scale.minimum, "scale.minimum", 0.001, 1_000);
  const maximum = asFiniteNumber(scale.maximum, "scale.maximum", minimum, 1_000);
  const initial = asFiniteNumber(scale.initial, "scale.initial", minimum, maximum);
  const step = asFiniteNumber(scale.step, "scale.step", 0.001, maximum - minimum || 0.001);

  const placement = asRecord(root.placement, "placement");
  exactKeys(placement, ["mode", "initialDistanceMeters"], "placement");
  if (placement.mode !== "horizontal") {
    throw new WebArError("launch_rejected", "Only horizontal placement is supported.", false);
  }

  return {
    schemaVersion: WEB_AR_SCHEMA_VERSION,
    launchGrantId: asId(root.launchGrantId, "launchGrantId"),
    composition: {
      compositionId: asId(composition.compositionId, "composition.compositionId"),
      revisionId: asId(composition.revisionId, "composition.revisionId"),
      configurationSha256: asSha256(
        composition.configurationSha256,
        "composition.configurationSha256"
      )
    },
    release: {
      releaseId: asId(release.releaseId, "release.releaseId"),
      manifestSha256: asSha256(release.manifestSha256, "release.manifestSha256")
    },
    model: {
      url: parseModelUrl(model.url, trustedModelOrigins),
      sha256: asSha256(model.sha256, "model.sha256"),
      byteLength: asInteger(model.byteLength, "model.byteLength", 20, 100 * 1024 * 1024),
      mediaType: "model/gltf-binary"
    },
    scale: { minimum, maximum, initial, step },
    placement: {
      mode: "horizontal",
      initialDistanceMeters: asFiniteNumber(
        placement.initialDistanceMeters,
        "placement.initialDistanceMeters",
        0.25,
        10
      )
    },
    origin,
    issuedAt: issuedAt.text,
    expiresAt: expiresAt.text
  };
}
